/**
 * Adapter vers l'API Electron exposée par preload.ts.
 * Fallback sans Electron (mode web) : aucune action, renvoie false.
 */

export interface ScannedFile {
  id: string;
  name: string;
  relativePath: string;
  absolutePath: string;
  size: number;
  extension: string;
  modifiedAt: string;
  preview: string;
}

export interface MoveResult {
  ok: boolean;
  newPath?: string;
  error?: string;
}

interface UniqueAPI {
  focus: {
    enterPip: () => Promise<boolean>;
    exitPip: () => Promise<boolean>;
  };
  apps: {
    open: (uri: string) => Promise<boolean>;
  };
  folder: {
    pick: () => Promise<string | null>;
    scan: (rootPath: string) => Promise<ScannedFile[]>;
    move: (
      rootPath: string,
      sourceAbsolutePath: string,
      targetFolderName: string,
    ) => Promise<MoveResult>;
  };
}

function getAPI(): UniqueAPI | null {
  const w = window as unknown as { uniqueAPI?: UniqueAPI };
  return w.uniqueAPI ?? null;
}

export const UniqueBridge = {
  isElectron(): boolean {
    return getAPI() !== null;
  },

  async enterFocusPip(): Promise<boolean> {
    return (await getAPI()?.focus.enterPip()) ?? false;
  },

  async exitFocusPip(): Promise<boolean> {
    return (await getAPI()?.focus.exitPip()) ?? false;
  },

  async openApp(uri: string): Promise<boolean> {
    const api = getAPI();
    if (api) return api.apps.open(uri);
    // Fallback web : tenter via window.open
    try {
      window.open(uri, '_self');
      return true;
    } catch {
      return false;
    }
  },

  async pickFolder(): Promise<string | null> {
    return (await getAPI()?.folder.pick()) ?? null;
  },

  async scanFolder(rootPath: string): Promise<ScannedFile[]> {
    return (await getAPI()?.folder.scan(rootPath)) ?? [];
  },

  async moveFile(
    rootPath: string,
    sourceAbsolutePath: string,
    targetFolderName: string,
  ): Promise<MoveResult> {
    return (
      (await getAPI()?.folder.move(rootPath, sourceAbsolutePath, targetFolderName)) ?? {
        ok: false,
        error: 'API Electron indisponible',
      }
    );
  },
};
